import React, { useState, useRef, useEffect, Suspense } from 'react';
import {
    Activity, Users, Calendar, Package, FileText, Settings, TrendingUp,
    Sparkles, AlertCircle, Plus, Edit, Trash2, Info, X, Bell, User, Check,
    ChevronLeft, ChevronRight, Search, LogOut, Menu, Receipt, CheckSquare, DollarSign, Lock, MapPin
} from 'lucide-react';

// Import Contexts
import { useAlert } from './contexts/AlertContext';
import { useAuth, PERMISSIONS } from './contexts/AuthContext';
import { usePatients, useAppointments, useStaff, useInventory } from './contexts/DataProviders';
import { useSettings } from './contexts/SettingsContext';

// Import API Service
import apiService from './apiService';

// Import extracted components
import AddPatientView from './AddPatientView';
import AddInventoryItemView from './AddInventoryItemView';
import AddStaffView from './AddStaffView';
import AddAppointmentView from './AddAppointmentView';
import POSView from './POSView';

// Import Update Components
import { PatientUpdateComponent, AppointmentUpdateComponent, StaffUpdateComponent, InventoryUpdateComponent } from './UpdateComponents';

// Import Info Components
import { PatientInfoComponent, AppointmentInfoComponent, StaffInfoComponent, InventoryInfoComponent } from './InfoComponents';

// Import Role-Specific Components
import DoctorDashboard from './DoctorDashboard';
import NurseTriage from './NurseTriage';
import ReceptionistCheckIn from './ReceptionistCheckIn';

// Import Enhanced Pharmacy Components

// Import Advanced Features
import AppointmentReminders from './AppointmentReminders';
import BulkOperations from './BulkOperations';
import AdvancedPatientSearch from './AdvancedPatientSearch';
import UserAdmin from './UserAdmin';
import SuperAdminDashboard from './SuperAdminDashboard';
import Invoices from './Invoices';
import ModuleUpgrade from './ModuleUpgrade';
import BillingManagement from './BillingManagement';
import MyInvoices from './MyInvoices';
import ChangePassword from './ChangePassword';
import QueueManagement from './QueueManagement';
import QueueDisplayBoard from './QueueDisplayBoard';
import LocationManagement from './LocationManagement';

// Import Notification Components
import { NotificationBell, NotificationPanel, useNotifications } from './contexts/NotificationContext';

// Import UI/UX Components
import { ThemeToggle, useTheme } from './contexts/ThemeContext';
import { useKeyboardShortcuts, KeyboardShortcutsHelp } from './hooks/useKeyboardShortcuts';
import { LoadingSpinner, PageLoading, SkeletonCard, SkeletonList } from './components/LoadingComponents';
import Tooltip from './components/Tooltip';
import { FadeIn, SlideIn, HoverAnimation } from './components/Animations';

// Lazy-loaded heavy modules (must come after all imports to satisfy import/first)
const PharmacistPrescriptions = React.lazy(() => import('./PharmacistPrescriptions'));
const PharmacyDashboard = React.lazy(() => import('./components/PharmacyDashboard'));
const PrescriptionManager = React.lazy(() => import('./components/PrescriptionManager'));
const ReportsDashboard = React.lazy(() => import('./ReportsDashboard'));

// Utility function for status colors
const getStatusColor = (status) => {
    switch (status) {
        case 'waiting': return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200';
        case 'in-progress': return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
        case 'completed': return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
        case 'available': return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
        case 'busy': return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
        case 'scheduled': return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
        case 'low-stock': return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
        case 'in-stock': return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
        default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
};

// --- Reusable Pagination Component ---
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
    return (
        <div className="flex justify-center items-center space-x-2 mt-6">
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <ChevronLeft className="w-5 h-5" />
            </button>
            {pageNumbers.map(number => (
                <button
                    key={number}
                    onClick={() => onPageChange(number)}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                        currentPage === number ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700 dark:text-gray-300 hover:bg-gray-300'
                    }`}
                >
                    {number}
                </button>
            ))}
            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <ChevronRight className="w-5 h-5" />
            </button>
        </div>
    );
};





// --- Reusable Search Bar Component ---
// --- Reusable Search Bar Component ---
const SearchBar = ({ searchTerm, searchField, onSearchChange, onClear, placeholder, resultCount, searchFields }) => {
  const [inputValue, setInputValue] = useState(searchTerm);
  const [selectedField, setSelectedField] = useState(searchField);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef(null);

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      setIsSearching(true);
      onSearchChange(inputValue, selectedField);
      setTimeout(() => setIsSearching(false), 300); // Simulate search delay
    }
  };

  // Handle Search button click
  const handleSearch = () => {
    setIsSearching(true);
    onSearchChange(inputValue, selectedField);
    setTimeout(() => setIsSearching(false), 300); // Simulate search delay
  };

  // Handle Clear button click
  const handleClear = () => {
    setInputValue('');
    setSelectedField('all');
    onClear();
    inputRef.current.focus();
  };

  // Sync input value and field with context when they change externally
  useEffect(() => {
    setInputValue(searchTerm);
    setSelectedField(searchField);
  }, [searchTerm, searchField]);

  return (
    <div className="relative mb-4 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <select
          value={selectedField}
          onChange={(e) => setSelectedField(e.target.value)}
          className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="all">All Fields</option>
          {searchFields.map(field => (
            <option key={field.value} value={field.value}>
              {field.label}
            </option>
          ))}
        </select>
        <div className="relative flex-1">
          <input
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            className="w-full p-3 pl-10 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
          {inputValue && (
            <button
              onClick={handleClear}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
              title="Clear search"
            >
              <X className="w-5 h-5" />
            </button>
          )}
          {isSearching && (
            <svg
              className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-blue-500 animate-spin"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          )}
        </div>
        <button
          onClick={handleSearch}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 flex items-center gap-2"
        >
          <Search className="w-4 h-4" />
          Search
        </button>
      </div>
      {resultCount !== undefined && (
        <p className="text-sm text-gray-600">
          {isSearching ? 'Searching...' : resultCount > 0 ? `${resultCount} results found` : 'No results found'}
        </p>
      )}
    </div>
  );
};

// --- Access Denied Message Component ---
const AccessDeniedMessage = ({ moduleName, onSettingsClick }) => (
        <div className="bg-white p-8 rounded-lg shadow-md text-center text-red-600">
        <AlertCircle className="w-12 h-12 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
        <p className="text-lg">The "{moduleName}" module is currently disabled for this practice size or in settings.</p>
        <p className="text-md mt-2 text-gray-600">
            You can enable it in the{' '}
            <button onClick={onSettingsClick} className="text-blue-600 hover:underline">
                Settings
            </button>{' '}
            page.
        </p>
    </div>
);

// --- Discharge Instructions Modal Component ---
const DischargeInstructionsModal = ({ showModal, onClose, patientName, instructions, isLoading }) => {
    if (!showModal) return null;
    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md md:max-w-lg lg:max-w-xl">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Discharge Instructions for {patientName}</h3>
                    <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400">
                        <X className="w-6 h-6" />
                    </button>
                </div>
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-8">
                        <svg className="animate-spin h-8 w-8 text-blue-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="text-gray-600">Generating discharge instructions...</p>
                    </div>
                ) : (
                    <div className="max-h-96 overflow-y-auto text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {instructions || 'No instructions generated.'}
                    </div>
                )}
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={onClose}
                        className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Medication Info Modal Component ---
const MedicationInfoModal = ({ showModal, onClose, medicationName, info, isLoading }) => {
    if (!showModal) return null;
    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md md:max-w-lg lg:max-w-xl">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Information for {medicationName}</h3>
                    <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400">
                        <X className="w-6 h-6" />
                    </button>
                </div>
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-8">
                        <svg className="animate-spin h-8 w-8 text-blue-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="text-gray-600">Fetching medication details...</p>
                    </div>
                ) : (
                    <div className="max-h-96 overflow-y-auto text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {info || 'No information available.'}
                    </div>
                )}
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={onClose}
                        className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Logout Confirmation Modal ---
const LogoutConfirmModal = ({ show, onConfirm, onCancel }) => {
    if (!show) return null;
    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Confirm Logout</h3>
                    <button onClick={onCancel} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400">
                        <X className="w-6 h-6" />
                    </button>
                </div>
                <p className="text-gray-600 mb-6">Are you sure you want to log out?</p>
                <div className="flex justify-end gap-3">
                    <button
                        onClick={onCancel}
                        className="bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
                    >
                        Log Out
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Confirm Delete Modal ---
const ConfirmDeleteModal = ({ show, onConfirm, onCancel, entityName, entityType }) => {
    if (!show) return null;
    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Confirm Delete {entityType}</h3>
                    <button onClick={onCancel} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400">
                        <X className="w-6 h-6" />
                    </button>
                </div>
                <p className="text-gray-600 mb-6">Are you sure you want to delete {entityType.toLowerCase()} <strong>{entityName}</strong>? This action cannot be undone.</p>
                <div className="flex justify-end gap-3">
                    <button
                        onClick={onCancel}
                        className="bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
};

// Main Application Component
const HospitalManagementAppContent = ({ onLogout }) => {
    const [currentView, setCurrentView] = useState('dashboard');
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteEntity, setDeleteEntity] = useState(null);
    const [deleteEntityType, setDeleteEntityType] = useState('');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false); // New state for sidebar visibility
    const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
    const { showAlert } = useAlert();
    const { isDarkMode } = useTheme();

    // Get authentication context
    const { currentUser, hasPermission, loading: authLoading } = useAuth();

    // Keyboard shortcuts
    useKeyboardShortcuts((action, data) => {
        switch (action) {
            case 'open-help':
                setShowKeyboardHelp(true);
                break;
            case 'toggle-theme':
                // Theme toggle is handled by ThemeContext
                break;
            case 'logout':
                setShowLogoutConfirm(true);
                break;
            case 'navigate':
                setCurrentView(data);
                break;
            default:
                // Handle other actions
                break;
        }
    });

    // Define permission checks for UI elements
    const canViewPatients = hasPermission(PERMISSIONS.VIEW_PATIENTS);
    const canAddPatients = hasPermission(PERMISSIONS.ADD_PATIENTS);
    const canViewAppointments = hasPermission(PERMISSIONS.VIEW_APPOINTMENTS);
    const canAddAppointments = hasPermission(PERMISSIONS.ADD_APPOINTMENTS);
    const canViewStaff = hasPermission(PERMISSIONS.VIEW_STAFF);
    const canAddStaff = hasPermission(PERMISSIONS.ADD_STAFF);
    const canViewInventory = hasPermission(PERMISSIONS.VIEW_INVENTORY);
    const canAddInventory = hasPermission(PERMISSIONS.ADD_INVENTORY);
    const canViewMedicalRecords = hasPermission(PERMISSIONS.VIEW_MEDICAL_RECORDS);
    const canAccessPOS = hasPermission(PERMISSIONS.CREATE_TRANSACTIONS);
    const canViewReports = hasPermission(PERMISSIONS.VIEW_REPORTS);
    const canViewSettings = hasPermission(PERMISSIONS.VIEW_SETTINGS);

    // Handle navigation and auto-close sidebar on mobile
    const handleNavigation = (view, fetchFn) => {
      setCurrentView(view);
      if (fetchFn) fetchFn();
      if (window.innerWidth < 768) setIsSidebarOpen(false); // Close sidebar on mobile
    };

    // Toggle sidebar collapsed state
    const toggleSidebarCollapse = () => {
      setIsSidebarCollapsed(!isSidebarCollapsed);
    };

    // Helper function to create navigation button classes
    const getNavButtonClasses = (isActive) => {
      return `flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-2'} p-2 w-full rounded-lg text-left transition-colors text-sm ${
        isActive ? 'bg-blue-500 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
      }`;
    };

const {
  patients,
  setPatients,
  addPatient,
  updatePatient,
  paginatedPatients,
  searchTerm: patientSearchTerm,
  setSearchTerm: setPatientSearchTerm,
  searchField: patientSearchField,
  setSearchField: setPatientSearchField,
  currentPage: patientCurrentPage,
  setCurrentPage: setPatientCurrentPage,
  totalPages: patientTotalPages,
} = usePatients();
const {
  appointments,
  setAppointments,
  addAppointment,
  updateAppointment,
  paginatedAppointments,
  searchTerm: appointmentSearchTerm,
  setSearchTerm: setAppointmentSearchTerm,
  searchField: appointmentSearchField,
  setSearchField: setAppointmentSearchField,
  currentPage: appointmentCurrentPage,
  setCurrentPage: setAppointmentCurrentPage,
  totalPages: appointmentTotalPages,
} = useAppointments();
const {
  staff,
  setStaff,
  addStaff,
  paginatedStaff,
  searchTerm: staffSearchTerm,
  setSearchTerm: setStaffSearchTerm,
  searchField: staffSearchField,
  setSearchField: setStaffSearchField,
  currentPage: staffCurrentPage,
  setCurrentPage: setStaffCurrentPage,
  totalPages: staffTotalPages,
} = useStaff();
const {
  inventoryItems,
  setInventoryItems,
  addInventoryItem,
  paginatedInventoryItems,
  searchTerm: inventorySearchTerm,
  setSearchTerm: setInventorySearchTerm,
  searchField: inventorySearchField,
  setSearchField: setInventorySearchField,
  currentPage: inventoryCurrentPage,
  setCurrentPage: setInventoryCurrentPage,
  totalPages: inventoryTotalPages,
} = useInventory();
const { appSettings: loadedAppSettings, practiceSize, setPracticeSize, updateSetting, isLoading } = useSettings();
const appSettings = loadedAppSettings || {};

    // Refs for update components
    const patientUpdateRef = useRef(null);
    const appointmentUpdateRef = useRef(null);
    const staffUpdateRef = useRef(null);
    const inventoryUpdateRef = useRef(null);

    // Refs for info components
    const patientInfoRef = useRef(null);
    const appointmentInfoRef = useRef(null);
    const staffInfoRef = useRef(null);
    const inventoryInfoRef = useRef(null);

    // States for LLM integrations
    const [showMedicationInfoModal, setShowMedicationInfoModal] = useState(false);
    const [selectedMedication, setSelectedMedication] = useState(null);
    const [medicationLlmInfo, setMedicationLlmInfo] = useState('');
    const [isGeneratingMedicationInfo, setIsGeneratingMedicationInfo] = useState(false);
    const [showDischargeInstructionsModal, setShowDischargeInstructionsModal] = useState(false);
    const [selectedPatientForDischarge, setSelectedPatientForDischarge] = useState(null);
    const [dischargeInstructionsLlmInfo, setDischargeInstructionsLlmInfo] = useState('');
    const [isGeneratingDischargeInstructions, setIsGeneratingDischargeInstructions] = useState(false);

    // Fetch data handlers
    const fetchPatients = async () => {
        try {
            const patientData = await apiService.getPatients();
            setPatients(patientData);
            showAlert('Patients data loaded successfully!', 'success');
        } catch (error) {
            console.error('Error fetching patients:', error);
            showAlert('Failed to load patients data. Please try again.', 'error');
        }
    };

    const fetchAppointments = async () => {
        try {
            const appointmentData = await apiService.getAppointments();
            if (!Array.isArray(appointmentData)) {
                throw new Error('Invalid appointment data structure');
            }
            setAppointments(appointmentData);
            showAlert('Appointments data loaded successfully!', 'success');
        } catch (error) {
            console.error('Error fetching appointments:', error);
            showAlert('Failed to load appointments data. Please try again.', 'error');
        }
    };

    const fetchStaff = async () => {
        try {
            const staffData = await apiService.getStaff();
            setStaff(staffData);
            showAlert('Staff data loaded successfully!', 'success');
        } catch (error) {
            console.error('Error fetching staff:', error);
            showAlert('Failed to load staff data. Please try again.', 'error');
        }
    };

    const fetchInventory = async () => {
        try {
            const inventoryData = await apiService.getInventoryItems();
            setInventoryItems(inventoryData);
            showAlert('Inventory data loaded successfully!', 'success');
        } catch (error) {
            console.error('Error fetching inventory:', error);
            showAlert('Failed to load inventory data. Please try again.', 'error');
        }
    };

    const handleDeleteStaff = async (id) => {
        const staffToDelete = staff.find(s => s.id === id);
        if (staffToDelete) {
            try {
                const mutation = `
                    mutation DeleteStaff($staffId: Int!) {
                        deleteStaff(staffId: $staffId) {
                            id
                        }
                    }
                `;
                const variables = { staffId: parseInt(id, 10) };
                const response = await apiService.graphqlRequest(mutation, variables);
                if (!response || !response.deleteStaff) {
                    throw new Error('Invalid response from server');
                }
                setStaff(staff.filter(s => s.id !== id));
                showAlert(`Staff member ${staffToDelete.name} deleted successfully.`, 'success');
            } catch (error) {
                console.error('Failed to delete staff:', error);
                showAlert('Failed to delete staff member.', 'error');
            }
        }
    };

    const handleDeleteAppointment = async (id) => {
        const appointmentToDelete = appointments.find(a => a.id === id);
        if (appointmentToDelete) {
            try {
                const mutation = `
                    mutation SoftDeleteAppointment($id: Int!) {
                        softDeleteAppointment(id: $id) {
                            id
                            patientId
                            doctorId
                            appointmentTime
                            department
                            appointmentType
                            status
                            notes
                            organization {
                                id
                                name
                            }
                        }
                    }
                `;
                const variables = { id: parseInt(id, 10) };
                const response = await apiService.graphqlRequest(mutation, variables);
                if (!response || !response.softDeleteAppointment) {
                    throw new Error('Invalid response from server');
                }
                setAppointments(appointments.filter(a => a.id !== id));
                const patient = patients.find(p => p.id === appointmentToDelete.patientId);
                showAlert(`Appointment for ${patient?.name || 'N/A'} deleted successfully.`, 'success');
            } catch (error) {
                console.error('Failed to delete appointment:', error);
                showAlert('Failed to delete appointment.', 'error');
            }
        }
    };

    const confirmDelete = (entity, type) => {
        setDeleteEntity(entity);
        setDeleteEntityType(type);
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = async () => {
        if (deleteEntityType === 'Staff') {
            await handleDeleteStaff(deleteEntity.id);
        } else if (deleteEntityType === 'Appointment') {
            await handleDeleteAppointment(deleteEntity.id);
        }
        setShowDeleteConfirm(false);
        setDeleteEntity(null);
        setDeleteEntityType('');
    };

    const handleAddAppointment = async (formData) => {
        const { patientId, doctorId, appointmentTime, department, appointmentType, status, notes, organizationId } = formData;

        if (!patientId || !doctorId || !appointmentTime || !appointmentType) {
            showAlert('Please fill in all required appointment fields.', 'error');
            return;
        }

        try {
            await addAppointment({
                patientId,
                doctorId: parseInt(doctorId, 10),
                appointmentTime,
                department: department || null,
                appointmentType: appointmentType || null,
                status: status || 'scheduled',
                notes: notes || null,
                organizationId: organizationId || appSettings.id,
            });
            showAlert('Appointment scheduled successfully!', 'success');
            setCurrentView('appointments');
        } catch (error) {
            console.error('Failed to add appointment:', error);
            showAlert('Failed to schedule appointment. Please try again.', 'error');
        }
    };

    const updatePatientStatus = async (id, newStatus) => {
        const patientToUpdate = patients.find(p => p.id === id);
        if (patientToUpdate) {
            try {
                const mutation = `
                    mutation UpdatePatient($patientId: String!, $status: String) {
                        updatePatient(patientId: $patientId, status: $status) {
                            id
                            patientId
                            status
                        }
                    }
                `;
                const variables = {
                    patientId: patientToUpdate.patientId,
                    status: newStatus,
                };
                const response = await apiService.graphqlRequest(mutation, variables);
                if (!response || !response.updatePatient) {
                    throw new Error('Invalid response from server');
                }
                setPatients(
                    patients.map((item) =>
                        item.patientId === patientToUpdate.patientId ? response.updatePatient : item
                    )
                );
                showAlert(`Patient ${patientToUpdate.name} status updated to ${newStatus}.`, 'info');
            } catch (error) {
                console.error('Failed to update patient status:', error);
                showAlert('Failed to update patient status.', 'error');
            }
        }
    };

    const updateAppointmentStatus = async (id, newStatus) => {
        const appointmentToUpdate = appointments.find((a) => a.id === id);
        if (appointmentToUpdate) {
            try {
                const mutation = `
                    mutation UpdateAppointment($id: Int!, $status: String) {
                        updateAppointment(id: $id, status: $status) {
                            id
                            patientId
                            doctorId
                            appointmentTime
                            department
                            appointmentType
                            status
                            notes
                            organization {
                                id
                                name
                            }
                        }
                    }
                `;
                const variables = {
                    id: parseInt(id, 10),
                    status: newStatus,
                };
                const response = await apiService.graphqlRequest(mutation, variables);
                if (!response || !response.updateAppointment) {
                    throw new Error('Invalid response from server');
                }
                setAppointments(
                    appointments.map((item) =>
                        item.id === response.updateAppointment.id ? response.updateAppointment : item
                    )
                );
                const patient = patients.find((p) => p.id === appointmentToUpdate.patientId);
                showAlert(
                    `Appointment for ${patient?.name || 'N/A'} status updated to ${newStatus}.`,
                    'info'
                );
            } catch (error) {
                console.error('Failed to update appointment status:', error);
                showAlert('Failed to update appointment status.', 'error');
            }
        } else {
            showAlert('Appointment not found.', 'error');
        }
    };

    const getMedicationInfo = async (medicationName) => {
        setSelectedMedication(medicationName);
        setShowMedicationInfoModal(true);
        setIsGeneratingMedicationInfo(true);
        setMedicationLlmInfo('');
        try {
            const text = await apiService.getMedicationInfoLLM(medicationName);
            setMedicationLlmInfo(text);
            showAlert(`Info for ${medicationName} fetched.`, 'success');
        } catch (error) {
            setMedicationLlmInfo('Could not retrieve medication information. Please try again.');
            showAlert('Error fetching medication info.', 'error');
        } finally {
            setIsGeneratingMedicationInfo(false);
        }
    };

    const generateDischargeInstructions = async (patientId) => {
        const patient = patients.find(p => p.id === patientId);
        if (!patient || !patient.medicalHistory) {
            showAlert("Patient or medical history not found for discharge instructions.", 'error');
            setDischargeInstructionsLlmInfo('Medical history is required to generate discharge instructions.');
            setShowDischargeInstructionsModal(true);
            return;
        }
        setSelectedPatientForDischarge(patient.name);
        setShowDischargeInstructionsModal(true);
        setIsGeneratingDischargeInstructions(true);
        setDischargeInstructionsLlmInfo('');
        try {
            const text = await apiService.generateDischargeInstructionsLLM(patient.name, patient.medicalHistory);
            setDischargeInstructionsLlmInfo(text);
            showAlert('Discharge instructions generated.', 'success');
        } catch (error) {
            setDischargeInstructionsLlmInfo('Could not generate discharge instructions. Please try again.');
            showAlert('Error generating discharge instructions.', 'error');
        } finally {
            setIsGeneratingDischargeInstructions(false);
        }
    };

    // --- View Components ---
    const DashboardView = () => (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs text-gray-600 dark:text-gray-400">Total Patients</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{patients.length}</p>
                        </div>
                        <Users className="w-6 h-6 text-blue-500" />
                    </div>
                </div>
                {practiceSize !== 'solo' && (
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs text-gray-600 dark:text-gray-400">Active Appointments</p>
                                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{appointments.filter(a => a.status !== 'completed').length}</p>
                            </div>
                            <Calendar className="w-6 h-6 text-green-500" />
                        </div>
                    </div>
                )}
                {appSettings.enableStaffManagement && (
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs text-gray-600 dark:text-gray-400">Available Staff</p>
                                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{staff.filter(s => s.status === 'available').length}</p>
                            </div>
                            <Activity className="w-6 h-6 text-purple-500" />
                        </div>
                    </div>
                )}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                                <p className="text-xs text-gray-600 dark:text-gray-400">Avg Wait Time</p>
                                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">12 min</p>
                        </div>
                        <Activity className="w-6 h-6 text-orange-500" />
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md">
                    <h3 className="text-base font-semibold mb-3">Recent Patients</h3>
                    <div className="space-y-2">
                        {patients.slice(0, 5).map(patient => (
                            <div key={patient.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                <div>
                                    <p className="text-sm font-medium">{patient.name}</p>
                                    <p className="text-xs text-gray-600">{patient.department}</p>
                                </div>
                                <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(patient.status)}`}>
                                    {patient.status}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
                {practiceSize !== 'solo' && (
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md">
                        <h3 className="text-base font-semibold mb-3">Today's Appointments</h3>
                        <div className="space-y-2">
                            {appointments.slice(0, 5).map(appointment => (
                                <div key={appointment.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                    <div>
                                        <p className="text-sm font-medium">{patients.find(p => p.id === appointment.patientId)?.name || 'N/A'}</p>
                                        <p className="text-xs text-gray-600">{new Date(appointment.appointmentTime).toLocaleTimeString()} - {staff.find(s => s.id === appointment.doctorId)?.name || 'N/A'}</p>
                                    </div>
                                    <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(appointment.status)}`}>
                                        {appointment.status}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );


    

const PatientsView = () => (
  <div className="space-y-6">
    <div className="flex justify-between items-center mb-4">
      <h2 className="text-2xl font-bold">Patient Management</h2>
      {canAddPatients && (
        <button
          onClick={() => handleNavigation('add-patient')}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600"
        >
          <Plus className="w-4 h-4" />
          Add Patient
        </button>
      )}
    </div>

    <SearchBar
      searchTerm={patientSearchTerm}
      searchField={patientSearchField}
      onSearchChange={(value, field) => {
        setPatientSearchTerm(value);
        setPatientSearchField(field);
        setPatientCurrentPage(1);
      }}
      onClear={() => {
        setPatientSearchTerm('');
        setPatientSearchField('all');
        setPatientCurrentPage(1);
      }}
      placeholder="Search patients..."
      resultCount={paginatedPatients.length}
      searchFields={[
        { value: 'name', label: 'Name' },
        { value: 'department', label: 'Department' },
        { value: 'phone', label: 'Phone' },
      ]}
    />

    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr>
            <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Patient</th>
            <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Patient ID</th>
            <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Age</th>
            <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Address</th>
            <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Department</th>
            <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Status</th>
            <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {paginatedPatients.length > 0 ? (
            paginatedPatients.map(patient => (
              <tr key={patient.id}>
                <td className="px-6 sm:px-2 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{patient.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{patient.phone}</div>
                  </div>
                </td>
                <td className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{patient.patientId}</td>
                <td className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{patient.age}</td>
                <td className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{patient.address}</td>
                <td className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{patient.department}</td>
                <td className="px-6 sm:px-2 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(patient.status)}`}>
                    {patient.status}
                  </span>
                </td>
                <td className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex gap-2">
                    <button
                      onClick={() => patientUpdateRef.current?.openModal(patient)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                      title="Edit Patient"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => patientInfoRef.current?.openModal(patient)}
                      className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300"
                      title="View Details"
                    >
                      <Info className="w-4 h-4" />
                    </button>
                    {patient.status === 'waiting' && (
                      <button
                        onClick={() => updatePatientStatus(patient.id, 'in_progress')}
                        className="text-orange-600 hover:text-orange-900"
                        title="Mark as In-Progress"
                      >
                        <Activity className="w-4 h-4" />
                      </button>
                    )}
                    {patient.status === 'in_progress' && (
                      <button
                        onClick={() => updatePatientStatus(patient.id, 'completed')}
                        className="text-teal-600 hover:text-teal-900"
                        title="Mark as Completed"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                    {appSettings.enableTelemedicine && (
                      <button
                        onClick={() => showAlert(`Starting teleconsultation with ${patient.name}`, 'info')}
                        className="text-purple-600 hover:text-purple-900"
                        title="Start Teleconsultation"
                      >
                        <User className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={7} className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-center">
                No patients found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>

    <Pagination
      currentPage={patientCurrentPage}
      totalPages={patientTotalPages}
      onPageChange={setPatientCurrentPage}
    />

    <DischargeInstructionsModal
      showModal={showDischargeInstructionsModal}
      onClose={() => setShowDischargeInstructionsModal(false)}
      patientName={selectedPatientForDischarge}
      instructions={dischargeInstructionsLlmInfo}
      isLoading={isGeneratingDischargeInstructions}
    />

    <PatientUpdateComponent
      ref={patientUpdateRef}
      apiService={apiService}
      alertService={{ showAlert }}
      patientContext={{ items: patients, setItems: setPatients }}
    />

    <PatientInfoComponent
      ref={patientInfoRef}
      apiService={apiService}
      alertService={{ showAlert }}
      patientContext={{ items: patients, setItems: setPatients }}
    />
  </div>
);

    

const AppointmentsView = () => (
  <div className="space-y-6">
    <div className="flex justify-between items-center mb-4">
      <h2 className="text-2xl font-bold">Appointment Scheduling</h2>
      {canAddAppointments && practiceSize !== 'solo' && (
        <div className="flex gap-2">
          <button
            onClick={() => handleNavigation('add-appointment')}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600"
          >
            <Plus className="w-4 h-4" />
            Schedule Appointment
          </button>
          <button
            onClick={() => handleNavigation('appointment-reminders')}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-700"
          >
            <Bell className="w-4 h-4" />
            Reminders
          </button>
        </div>
      )}
    </div>
    {practiceSize === 'solo' ? (
      <AccessDeniedMessage moduleName="Appointment Scheduling" onSettingsClick={() => handleNavigation('settings')} />
    ) : (
      <>
        <SearchBar
          searchTerm={appointmentSearchTerm}
          searchField={appointmentSearchField}
          onSearchChange={(value, field) => {
            setAppointmentSearchTerm(value);
            setAppointmentSearchField(field);
            setAppointmentCurrentPage(1);
          }}
          onClear={() => {
            setAppointmentSearchTerm('');
            setAppointmentSearchField('all');
            setAppointmentCurrentPage(1);
          }}
          placeholder="Search appointments..."
          resultCount={paginatedAppointments.length}
          searchFields={[
            { value: 'patient', label: 'Patient' },
            { value: 'doctor', label: 'Doctor' },
            { value: 'department', label: 'Department' },
          ]}
        />
       
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Patient</th>
                <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Patient ID</th>
                <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Doctor</th>
                <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Department</th>
                <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Date & Time</th>
                <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Status</th>
                <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedAppointments.length > 0 ? (
                paginatedAppointments.map(appointment => (
                  <tr key={appointment.id}>
                    {/* Updated patient name display */}
                    <td className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                      {appointment.patient?.name || 'N/A'}
                    </td>
                    {/* Added patient ID column */}
                    <td className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {appointment.patient?.patientId || 'N/A'}
                    </td>
                    {/* Updated doctor name display */}
                    <td className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {appointment.doctor?.name || 'N/A'}
                    </td>
                    <td className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {appointment.department}
                    </td>
                    <td className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {new Date(appointment.appointmentTime).toLocaleString()}
                    </td>
                    <td className="px-6 sm:px-2 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(appointment.status)}`}>
                        {appointment.status}
                      </span>
                    </td>
                    <td className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button
                          onClick={() => appointmentUpdateRef.current?.openModal(appointment)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                          title="Edit Appointment"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => appointmentInfoRef.current?.openModal(appointment)}
                          className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300"
                          title="View Details"
                        >
                          <Info className="w-4 h-4" />
                        </button>
                        {appointment.status === 'scheduled' && (
                          <button
                            onClick={() => updateAppointmentStatus(appointment.id, 'completed')}
                            className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300"
                            title="Mark as Completed"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => confirmDelete(appointment, 'Appointment')}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                          title="Delete Appointment"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-center">
                    No appointments found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination currentPage={appointmentCurrentPage} totalPages={appointmentTotalPages} onPageChange={setAppointmentCurrentPage} />
        <AppointmentUpdateComponent
          ref={appointmentUpdateRef}
          apiService={apiService}
          alertService={{ showAlert }}
          appointmentContext={{ items: appointments, setItems: setAppointments }}
        />
        <AppointmentInfoComponent
          ref={appointmentInfoRef}
          apiService={apiService}
          alertService={{ showAlert }}
          appointmentContext={{ items: appointments, setItems: setAppointments }}
        />
      </>
    )}
  </div>
);

    const StaffView = () => (
        <div className="space-y-6">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold">Staff Management</h2>
                {canAddStaff && appSettings.enableStaffManagement && (
                    <button
                        onClick={() => handleNavigation('add-staff')}
                        className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600"
                    >
                        <Plus className="w-4 h-4" />
                        Add Staff
                    </button>
                )}
            </div>
            {!appSettings.enableStaffManagement ? (
                <AccessDeniedMessage moduleName="Staff Management" onSettingsClick={() => handleNavigation('settings')} />
            ) : (
                <>
<SearchBar
  searchTerm={staffSearchTerm}
  searchField={staffSearchField}
  onSearchChange={(value, field) => {
    setStaffSearchTerm(value);
    setStaffSearchField(field);
    setStaffCurrentPage(1);
  }}
  onClear={() => {
    setStaffSearchTerm('');
    setStaffSearchField('all');
    setStaffCurrentPage(1);
  }}
  placeholder="Search staff..."
  resultCount={paginatedStaff.length}
  searchFields={[
    { value: 'name', label: 'Name' },
    { value: 'specialization', label: 'Specialization' },
    { value: 'department', label: 'Department' },
  ]}
/>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                    <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Name</th>
                                    <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Phone</th>
                                    <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Role</th>
                                    <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Department</th>
                                    <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Status</th>
                                    <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {paginatedStaff.length > 0 ? (
                                    paginatedStaff.map(member => (
                                        <tr key={member.id}>
                                            <td className="px-6 sm:px-2 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{member.name}</div>
                                                {member.specialization && (
                                                    <div className="text-sm text-gray-500 dark:text-gray-400">{member.specialization}</div>
                                                )}
                                            </td>
                                            <td className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{member.phone || 'N/A'}</td>
                                            <td className="px-6 sm:px-2 py-4 whitespace-nowrap">
                                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">
                                                    {member.role || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{member.department}</td>
                                            <td className="px-6 sm:px-2 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(member.status)}`}>
                                                    {member.status}
                                                </span>
                                            </td>
                                            <td className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => staffUpdateRef.current?.openModal(member)}
                                                        className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                                                        title="Edit Staff"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => staffInfoRef.current?.openModal(member)}
                                                        className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300"
                                                        title="View Details"
                                                    >
                                                        <Info className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => confirmDelete(member, 'Staff')}
                                                        className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                                                        title="Delete Staff"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-center">
                                            No staff found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={staffCurrentPage} totalPages={staffTotalPages} onPageChange={setStaffCurrentPage} />
                    <StaffUpdateComponent
                        ref={staffUpdateRef}
                        apiService={apiService}
                        alertService={{ showAlert }}
                        staffContext={{ items: staff, setItems: setStaff }}
                    />
                    <StaffInfoComponent
                        ref={staffInfoRef}
                        apiService={apiService}
                        alertService={{ showAlert }}
                        staffContext={{ items: staff, setItems: setStaff }}
                    />
                </>
            )}
        </div>
    );

    const InventoryView = () => (
        <div className="space-y-6">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold">Inventory Management</h2>
                {canAddInventory && appSettings.enableInventoryManagement && (
                    <button
                        onClick={() => handleNavigation('add-inventory-item')}
                        className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600"
                    >
                        <Plus className="w-4 h-4" />
                        Add Item
                    </button>
                )}
            </div>
            {!appSettings.enableInventoryManagement ? (
                <AccessDeniedMessage moduleName="Inventory Management" onSettingsClick={() => handleNavigation('settings')} />
            ) : (
                <>
<SearchBar
  searchTerm={inventorySearchTerm}
  searchField={inventorySearchField}
  onSearchChange={(value, field) => {
    setInventorySearchTerm(value);
    setInventorySearchField(field);
    setInventoryCurrentPage(1);
  }}
  onClear={() => {
    setInventorySearchTerm('');
    setInventorySearchField('all');
    setInventoryCurrentPage(1);
  }}
  placeholder="Search inventory..."
  resultCount={paginatedInventoryItems.length}
  searchFields={[
    { value: 'name', label: 'Name' },
    { value: 'type', label: 'Type' },
    { value: 'location', label: 'Location' },
  ]}
/>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                    <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Item Name</th>
                                    {/* <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Type</th> */}
                                    <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Quantity</th>
                                    <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Expiry Date</th>
                                    <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Location</th>
                                    <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Status</th>
                                    <th className="px-6 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {paginatedInventoryItems.length > 0 ? (
                                    paginatedInventoryItems.map(item => (
                                        <tr key={item.id}>
                                            <td className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">{item.name}</td>
                                            {/* <td className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{item.type}</td> */}
                                            <td className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{item.quantity} {item.unit}</td>
                                            <td className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{new Date(item.expiryDate).toLocaleDateString()}</td>
                                            <td className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{item.location}</td>
                                            <td className="px-6 sm:px-2 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(item.status)}`}>
                                                    {item.status}
                                                </span>
                                            </td>
                                            <td className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => inventoryUpdateRef.current?.openModal(item)}
                                                        className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                                                        title="Edit Item"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => inventoryInfoRef.current?.openModal(item)}
                                                        className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300"
                                                        title="View Details"
                                                    >
                                                        <Info className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => getMedicationInfo(item.name)}
                                                        disabled={isGeneratingMedicationInfo}
                                                        className={`text-purple-600 hover:text-purple-900 flex items-center gap-1 ${
                                                            isGeneratingMedicationInfo ? 'opacity-50 cursor-not-allowed' : ''
                                                        }`}
                                                        title="Get Medication Info"
                                                    >
                                                        {/* <Info className="w-4 h-4" /> ✨ */}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="px-6 sm:px-2 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-center">
                                            No inventory items found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={inventoryCurrentPage} totalPages={inventoryTotalPages} onPageChange={setInventoryCurrentPage} />
                    <MedicationInfoModal
                        showModal={showMedicationInfoModal}
                        onClose={() => setShowMedicationInfoModal(false)}
                        medicationName={selectedMedication}
                        info={medicationLlmInfo}
                        isLoading={isGeneratingMedicationInfo}
                    />
                    <InventoryUpdateComponent
                        ref={inventoryUpdateRef}
                        apiService={apiService}
                        alertService={{ showAlert }}
                        inventoryContext={{ items: inventoryItems, setItems: setInventoryItems }}
                    />
                    <InventoryInfoComponent
                        ref={inventoryInfoRef}
                        apiService={apiService}
                        alertService={{ showAlert }}
                        inventoryContext={{ items: inventoryItems, setItems: setInventoryItems }}
                    />
                </>
            )}
        </div>
    );

    

    const SettingsView = () => {
        const handleSettingToggle = (key) => {
            updateSetting(key, !appSettings[key]);
            showAlert(`${key} is now ${!appSettings[key] ? 'enabled' : 'disabled'}.`, 'success');
        };
        if (isLoading) {
            return (
                <div className="flex justify-center items-center h-64">
                    <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="ml-3 text-gray-600">Loading settings...</p>
                </div>
            );
        }
        return (
            <div className="space-y-6 bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Application Settings</h2>
                <div className="space-y-4">
                    <div>
                        <label htmlFor="organizationName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Organization Name</label>
                        <input
                            type="text"
                            id="organizationName"
                            value={appSettings.organizationName || ''}
                            onChange={(e) => updateSetting('organizationName', e.target.value)}
                            className="mt-1 block w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div>
                        <label htmlFor="practiceSize" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Organization Type</label>
                        <input
                            type="text"
                            id="practiceSize"
                            value={practiceSize === 'solo' ? 'Solo Practice' : practiceSize === 'clinic' ? 'Clinic' : practiceSize === 'hospital' ? 'Hospital' : practiceSize === 'pharmacy' ? 'Pharmacy' : practiceSize}
                            disabled
                            className="mt-1 block w-full border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-md shadow-sm p-2 cursor-not-allowed"
                        />
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                            Organization type is set by your administrator and cannot be changed here.
                        </p>
                    </div>
                    {/* Quick Links for Admins */}
                    {['superadmin', 'org_admin'].includes(currentUser?.role) && (
                        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <h3 className="text-sm font-semibold text-gray-800 mb-3">Management Tools</h3>
                            <button
                                onClick={() => handleNavigation('location-management')}
                                className="w-full text-left px-4 py-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center justify-between group"
                            >
                                <div className="flex items-center gap-3">
                                    <MapPin className="w-5 h-5 text-blue-600" />
                                    <div>
                                        <p className="font-medium text-gray-900">Locations & Rooms</p>
                                        <p className="text-xs text-gray-500">Manage hospital rooms and service locations</p>
                                    </div>
                                </div>
                                <span className="text-gray-400 group-hover:text-gray-600">→</span>
                            </button>
                        </div>
                    )}
                    
                    <div className="space-y-3">
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 pt-2">Module Management</h3>
                        <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700">
                            <label htmlFor="enableTelemedicine" className="text-gray-700 dark:text-gray-300 flex-1">Enable Telemedicine</label>
                            <input
                                type="checkbox"
                                id="enableTelemedicine"
                                checked={appSettings.enableTelemedicine || false}
                                onChange={() => handleSettingToggle('enableTelemedicine')}
                                className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                            />
                        </div>
                        <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700">
                            <label htmlFor="enableDetailedMedicalHistory" className="text-gray-700 dark:text-gray-300 flex-1">Enable Detailed Medical History</label>
                            <input
                                type="checkbox"
                                id="enableDetailedMedicalHistory"
                                checked={appSettings.enableDetailedMedicalHistory || false}
                                onChange={() => handleSettingToggle('enableDetailedMedicalHistory')}
                                className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                            />
                        </div>
                        <div className={`p-3 border rounded-md ${practiceSize === 'solo' ? 'bg-gray-100 text-gray-500 dark:text-gray-400' : 'border-gray-200 dark:border-gray-600'}`}>
                            <div className="flex items-center justify-between">
                                <label htmlFor="enableStaffManagement" className={`flex-1 text-gray-700 dark:text-gray-300 ${practiceSize === 'solo' ? 'cursor-not-allowed' : ''}`}>Enable Staff Management</label>
                                <input
                                    type="checkbox"
                                    id="enableStaffManagement"
                                    checked={appSettings.enableStaffManagement || false}
                                    onChange={() => handleSettingToggle('enableStaffManagement')}
                                    disabled={practiceSize === 'solo'}
                                    className={`h-5 w-5 text-blue-600 rounded focus:ring-blue-500 ${practiceSize === 'solo' ? 'opacity-50 cursor-not-allowed' : ''}`}
                                />
                            </div>
                            {practiceSize === 'solo' && (
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Not available for Solo Practice organization type.</p>
                            )}
                        </div>
                        <div className={`p-3 border rounded-md ${practiceSize === 'solo' ? 'bg-gray-100 text-gray-500 dark:text-gray-400' : 'border-gray-200 dark:border-gray-600'}`}>
                            <div className="flex items-center justify-between">
                                <label htmlFor="enableInventoryManagement" className={`flex-1 text-gray-700 dark:text-gray-300 ${practiceSize === 'solo' ? 'cursor-not-allowed' : ''}`}>Enable Inventory & Pharmacy</label>
                                <input
                                    type="checkbox"
                                    id="enableInventoryManagement"
                                    checked={appSettings.enableInventoryManagement || practiceSize === 'pharmacy'}
                                    onChange={() => handleSettingToggle('enableInventoryManagement')}
                                    disabled={practiceSize === 'solo' || practiceSize === 'pharmacy'}
                                    className={`h-5 w-5 text-blue-600 rounded focus:ring-blue-500 ${(practiceSize === 'solo' || practiceSize === 'pharmacy') ? 'opacity-50 cursor-not-allowed' : ''}`}
                                />
                            </div>
                            {practiceSize === 'solo' && (
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Not available for Solo Practice organization type.</p>
                            )}
                            {practiceSize === 'pharmacy' && (
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Always enabled for Pharmacy organization type.</p>
                            )}
                        </div>
                        <div className={`p-3 border rounded-md ${practiceSize === 'solo' ? 'bg-gray-100 text-gray-500 dark:text-gray-400' : 'border-gray-200 dark:border-gray-600'}`}>
                            <div className="flex items-center justify-between">
                                <label htmlFor="enableReports" className={`flex-1 text-gray-700 dark:text-gray-300 ${practiceSize === 'solo' ? 'cursor-not-allowed' : ''}`}>Enable Analytics & Reports</label>
                                <input
                                    type="checkbox"
                                    id="enableReports"
                                    checked={appSettings.enableReports || false}
                                    onChange={() => handleSettingToggle('enableReports')}
                                    disabled={practiceSize === 'solo'}
                                    className={`h-5 w-5 text-blue-600 rounded focus:ring-blue-500 ${practiceSize === 'solo' ? 'opacity-50 cursor-not-allowed' : ''}`}
                                />
                            </div>
                            {practiceSize === 'solo' && (
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Not available for Solo Practice organization type.</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const formatViewName = (view) => {
        switch (view) {
            case 'dashboard': return 'Dashboard';
            case 'patients': return 'Patient Management';
            case 'add-patient': return 'Add New Patient';
            case 'appointments': return 'Appointment Scheduling';
            case 'add-appointment': return 'Schedule Appointment';
            case 'staff': return 'Staff Management';
            case 'add-staff': return 'Add New Staff Member';
            case 'inventory': return 'Inventory Management';
            case 'add-inventory-item': return 'Add Inventory Item';
            case 'reports': return 'Analytics & Reports';
            case 'settings': return 'Settings';
            case 'change-password': return 'Change Password';
            case 'queue-management': return 'Queue Management';
            case 'queue-display': return 'Queue Display Board';
            default: return 'Hospital Management';
        }
    };

    const renderView = () => {
        if (isLoading) {
            return (
                <div className="flex justify-center items-center h-96">
                    <svg className="animate-spin h-10 w-10 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="ml-3 text-lg text-gray-600">Loading application settings and data...</p>
                </div>
            );
        }
        // Role-specific dashboard routing
        if (currentView === 'dashboard') {
            switch (currentUser?.role) {
                case 'doctor':
                    return <DoctorDashboard onNavigate={setCurrentView} />;
                case 'nurse':
                    return <NurseTriage onNavigate={setCurrentView} />;
                case 'receptionist':
                    return <ReceptionistCheckIn onNavigate={setCurrentView} />;
                case 'pharmacist':
                    return <PharmacistPrescriptions onNavigate={setCurrentView} />;
                default:
                    return <DashboardView />;
            }
        }

        switch (currentView) {
            case 'patients': return <PatientsView />;
            case 'add-patient': return <AddPatientView setCurrentView={setCurrentView} addPatient={addPatient} />;
            case 'appointments': return <AppointmentsView />;
            case 'add-appointment': return <AddAppointmentView onAddAppointment={handleAddAppointment} onCancel={() => handleNavigation('appointments')} />;
            case 'staff': return <StaffView />;
            case 'add-staff': return <AddStaffView setCurrentView={setCurrentView} addStaff={addStaff} />;
            case 'inventory': return <InventoryView />;
            case 'add-inventory-item': return <AddInventoryItemView setCurrentView={setCurrentView} addInventoryItem={addInventoryItem} />;
            case 'pharmacy':
                if (!appSettings.enableInventoryManagement && currentUser?.role !== 'superadmin') {
                    return <AccessDeniedMessage moduleName="Inventory & Pharmacy" onSettingsClick={() => handleNavigation('settings')} />;
                }
                if (!['pharmacist','org_admin','superadmin'].includes(currentUser?.role)) {
                    return <AccessDeniedMessage moduleName="Pharmacy" onSettingsClick={() => handleNavigation('settings')} />;
                }
                return (
                    <Suspense fallback={<div className="p-6">Loading pharmacy…</div>}>
                        <PharmacyDashboard onNavigate={setCurrentView} />
                    </Suspense>
                );
            case 'prescriptions':
                if (!appSettings.enableInventoryManagement && currentUser?.role !== 'superadmin') {
                    return <AccessDeniedMessage moduleName="Pharmacy" onSettingsClick={() => handleNavigation('settings')} />;
                }
                if (!['pharmacist','doctor','org_admin','superadmin'].includes(currentUser?.role)) {
                    return <AccessDeniedMessage moduleName="Prescriptions" onSettingsClick={() => handleNavigation('settings')} />;
                }
                return (
                    <Suspense fallback={<div className="p-6">Loading prescriptions…</div>}>
                        <PrescriptionManager onNavigate={setCurrentView} />
                    </Suspense>
                );
            case 'reports': return (
                <Suspense fallback={<div className="p-6">Loading reports…</div>}>
                    <ReportsDashboard onNavigate={setCurrentView} />
                </Suspense>
            );
            case 'appointment-reminders': return <AppointmentReminders onNavigate={setCurrentView} />;
            case 'bulk-operations': return <BulkOperations onNavigate={setCurrentView} />;
            case 'invoices':
                if (currentUser?.role !== 'superadmin') {
                    return <AccessDeniedMessage moduleName="Invoice Management" onSettingsClick={() => handleNavigation('settings')} />;
                }
                return <Invoices />;
            case 'my-invoices':
                if (!['org_admin','cashier'].includes(currentUser?.role)) {
                    return <AccessDeniedMessage moduleName="My Invoices" onSettingsClick={() => handleNavigation('settings')} />;
                }
                return <MyInvoices />;
            case 'user-admin':
                if (!['org_admin'].includes(currentUser?.role)) {
                    return <AccessDeniedMessage moduleName="User Administration" onSettingsClick={() => handleNavigation('settings')} />;
                }
                return <UserAdmin onNavigate={setCurrentView} />;
            case 'superadmin':
                if (currentUser?.role !== 'superadmin') {
                    return <AccessDeniedMessage moduleName="Superadmin" onSettingsClick={() => handleNavigation('settings')} />;
                }
                return <SuperAdminDashboard />;
            case 'billing':
                if (currentUser?.role !== 'superadmin') {
                    return <AccessDeniedMessage moduleName="Billing Management" onSettingsClick={() => handleNavigation('settings')} />;
                }
                return <BillingManagement />;
            case 'upgrade':
                if (!['org_admin'].includes(currentUser?.role)) {
                    return <AccessDeniedMessage moduleName="Module Upgrades" onSettingsClick={() => handleNavigation('settings')} />;
                }
                return <ModuleUpgrade />;
            case 'advanced-search': return <AdvancedPatientSearch onNavigate={setCurrentView} />;
            case 'settings': return <SettingsView />;
            case 'location-management': return <LocationManagement />;
            case 'change-password': return <ChangePassword />;
            case 'queue-management': return <QueueManagement />;
            case 'queue-display': return <QueueDisplayBoard location="consultation" facilityName={currentUser?.organization?.name || 'Hospital'} />;
            case 'pos': return <POSView setCurrentView={setCurrentView} />;
            default: return <DashboardView />;
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 flex">
            {/* Sidebar Backdrop (mobile only) */}
            {isSidebarOpen && (
              <div
                className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
                onClick={() => setIsSidebarOpen(false)}
              />
            )}

            {/* Sidebar Navigation */}
            <nav
              className={`${isSidebarCollapsed ? 'w-16' : 'w-64'} md:${isSidebarCollapsed ? 'w-16' : 'w-64'} bg-white dark:bg-gray-800 shadow-lg p-4 flex flex-col fixed inset-y-0 left-0 z-50 transition-all duration-300 ease-in-out ${
                isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
              }`}
            >
                <div className={`flex items-center mb-6 ${isSidebarCollapsed ? 'justify-center' : 'justify-between'}`}>
                    {!isSidebarCollapsed && (
                        <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                            Lipay Afya
                        </div>
                    )}
                    {isSidebarCollapsed && (
                        <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                            LA
                        </div>
                    )}
                    <button
                        onClick={toggleSidebarCollapse}
                        className="hidden md:block p-1 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                        title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    >
                        <ChevronLeft className={`w-4 h-4 transition-transform duration-300 ${isSidebarCollapsed ? 'rotate-180' : ''}`} />
                    </button>
                </div>
                <ul className="space-y-2 flex-1">
                    {/* Dashboard/Check-In - Different labels and behavior for receptionists */}
                    <li>
                        <button
                            onClick={() => handleNavigation('dashboard')}
                            className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-2'} p-2 w-full rounded-lg text-left transition-colors text-sm ${
                                currentView === 'dashboard' ? 'bg-blue-500 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                            title={isSidebarCollapsed ? (currentUser?.role === 'receptionist' ? 'Check-In' : 'Dashboard') : ''}
                        >
                            <Activity className="w-4 h-4" />
                            {!isSidebarCollapsed && (currentUser?.role === 'receptionist' ? 'Check-In' : 'Dashboard')}
                        </button>
                    </li>

                    {/* Patients - Available to all roles with view permission */}
                    {canViewPatients && (
                        <li>
                            <button
                                onClick={() => handleNavigation('patients', fetchPatients)}
                            className={getNavButtonClasses(currentView === 'patients' || currentView === 'add-patient')}
                            title={isSidebarCollapsed ? 'Patients' : ''}
                        >
                            <Users className="w-4 h-4" />
                            {!isSidebarCollapsed && 'Patients'}
                            </button>
                        </li>
                    )}
                    
                    {/* Triage - Nurse specific */}
                    {currentUser?.role === 'nurse' && practiceSize !== 'solo' && (
                        <li>
                            <button
                                onClick={() => handleNavigation('triage')}
                            className={getNavButtonClasses(currentView === 'triage')}
                            title={isSidebarCollapsed ? 'Triage' : ''}
                        >
                            <Activity className="w-4 h-4" />
                            {!isSidebarCollapsed && 'Triage'}
                            </button>
                        </li>
                    )}

                    {/* Point of Sale - Only for receptionist and pharmacist (not doctors/nurses) */}
                    {canAccessPOS && appSettings.enableInventoryManagement && (currentUser?.role === 'receptionist' || currentUser?.role === 'pharmacist' || currentUser?.role === 'superadmin' || currentUser?.role === 'org_admin') && (
                        <li>
                            <button
                                onClick={() => handleNavigation('pos', fetchInventory)}
                            className={getNavButtonClasses(currentView === 'pos')}
                            title={isSidebarCollapsed ? (currentUser?.role === 'receptionist' ? 'Billing' : 'Point of Sale') : ''}
                        >
                            <Receipt className="w-4 h-4" />
                            {!isSidebarCollapsed && (currentUser?.role === 'receptionist' ? 'Billing' : 'Point of Sale')}
                            </button>
                        </li>
                    )}

                    {/* Appointments - For authorized roles including nurses */}
                    {canViewAppointments && practiceSize !== 'solo' && (
                        <li>
                            <button
                                onClick={() => handleNavigation('appointments', fetchAppointments)}
                            className={getNavButtonClasses(currentView === 'appointments' || currentView === 'add-appointment')}
                            title={isSidebarCollapsed ? 'Appointments' : ''}
                        >
                            <Calendar className="w-4 h-4" />
                            {!isSidebarCollapsed && 'Appointments'}
                        </button>
                        </li>
                    )}

                    {/* Staff Management - Only for admins (doctors/nurses/receptionists/pharmacists don't need it) */}
                    {practiceSize !== 'solo' && canViewStaff && appSettings.enableStaffManagement && (currentUser?.role === 'superadmin' || currentUser?.role === 'org_admin') && (
                        <li>
                            <button
                                onClick={() => handleNavigation('staff', fetchStaff)}
                            className={getNavButtonClasses(currentView === 'staff' || currentView === 'add-staff')}
                            title={isSidebarCollapsed ? 'Staff' : ''}
                        >
                            <User className="w-4 h-4" />
                            {!isSidebarCollapsed && 'Staff'}
                            </button>
                        </li>
                    )}

                    {/* Inventory - Only for pharmacist, nurse, and admins (not receptionist or doctor) */}
                    {canViewInventory && (appSettings.enableInventoryManagement || currentUser?.role === 'superadmin') && 
                     (currentUser?.role === 'pharmacist' || currentUser?.role === 'nurse' || currentUser?.role === 'superadmin' || currentUser?.role === 'org_admin') && (
                        <li>
                            <button
                                onClick={() => handleNavigation('inventory')}
                            className={getNavButtonClasses(currentView === 'inventory' || currentView === 'add-inventory-item')}
                            title={isSidebarCollapsed ? 'Inventory' : ''}
                        >
                            <Package className="w-4 h-4" />
                            {!isSidebarCollapsed && 'Inventory'}
                            </button>
                        </li>
                    )}

                    {/* Pharmacy - Hide for solo practice, pharmacy-specific roles */}
                    {practiceSize !== 'solo' && (currentUser?.role === 'pharmacist' || currentUser?.role === 'superadmin' || currentUser?.role === 'org_admin') && (
                        <li>
                            <button
                                onClick={() => handleNavigation('pharmacy')}
                            className={getNavButtonClasses(currentView === 'pharmacy' || currentView === 'prescriptions')}
                            title={isSidebarCollapsed ? 'Pharmacy' : ''}
                        >
                            <FileText className="w-4 h-4" />
                            {!isSidebarCollapsed && 'Pharmacy'}
                            </button>
                        </li>
                    )}

                    {/* Reports - Hide for solo practice */}
                    {practiceSize !== 'solo' && canViewReports && (appSettings.enableReports || currentUser?.role === 'superadmin') && (
                        <li>
                            <button
                                onClick={() => handleNavigation('reports')}
                            className={getNavButtonClasses(currentView === 'reports')}
                            title={isSidebarCollapsed ? 'Reports' : ''}
                        >
                            <TrendingUp className="w-4 h-4" />
                            {!isSidebarCollapsed && 'Reports'}
                            </button>
                        </li>
                    )}


                    {/* System Administration - Superadmin only */}
                    {currentUser?.role === 'superadmin' && (
                        <>
                            <li>
                                <button
                                    onClick={() => handleNavigation('superadmin')}
                            className={getNavButtonClasses(currentView === 'superadmin')}
                            title={isSidebarCollapsed ? 'Organizations' : ''}
                        >
                            <Settings className="w-4 h-4" />
                            {!isSidebarCollapsed && 'Organizations'}
                            </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => handleNavigation('billing')}
                            className={getNavButtonClasses(currentView === 'billing')}
                            title={isSidebarCollapsed ? 'Billing' : ''}
                        >
                            <DollarSign className="w-4 h-4" />
                            {!isSidebarCollapsed && 'Billing'}
                            </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => handleNavigation('invoices')}
                            className={getNavButtonClasses(currentView === 'invoices')}
                            title={isSidebarCollapsed ? 'Invoices' : ''}
                        >
                            <FileText className="w-4 h-4" />
                            {!isSidebarCollapsed && 'Invoices'}
                            </button>
                            </li>
                        </>
                    )}

                    {/* Administration - Org Admin */}
                    {currentUser?.role === 'org_admin' && (
                        <>
                            <li>
                                <button
                                    onClick={() => handleNavigation('user-admin')}
                                    className={`flex items-center gap-3 p-3 w-full rounded-lg text-left transition-colors ${
                                        currentView === 'user-admin' ? 'bg-blue-500 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                    }`}
                                >
                                    <Users className="w-5 h-5" />
                                    Administration
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => handleNavigation('my-invoices')}
                            className={getNavButtonClasses(currentView === 'my-invoices')}
                            title={isSidebarCollapsed ? 'My Invoices' : ''}
                        >
                            <FileText className="w-4 h-4" />
                            {!isSidebarCollapsed && 'My Invoices'}
                            </button>
                            </li>
                        </>
                    )}


                    {/* Settings - Only for admin roles (receptionist can't change settings anyway) */}
                    {canViewSettings && (currentUser?.role === 'superadmin' || currentUser?.role === 'org_admin') && (
                        <li>
                            <button
                                onClick={() => handleNavigation('settings')}
                            className={getNavButtonClasses(currentView === 'settings')}
                            title={isSidebarCollapsed ? 'Settings' : ''}
                        >
                            <Settings className="w-4 h-4" />
                            {!isSidebarCollapsed && 'Settings'}
                            </button>
                        </li>
                    )}

                    {/* Queue Management - Available to clinical and administrative staff */}
                    {(currentUser?.role === 'receptionist' || currentUser?.role === 'org_admin' || currentUser?.role === 'superadmin' || currentUser?.role === 'doctor' || currentUser?.role === 'nurse') && practiceSize !== 'solo' && (
                        <li>
                            <button
                                onClick={() => handleNavigation('queue-management')}
                            className={getNavButtonClasses(currentView === 'queue-management')}
                            title={isSidebarCollapsed ? 'Queue Management' : ''}
                        >
                            <Users className="w-4 h-4" />
                            {!isSidebarCollapsed && 'Queue Management'}
                            </button>
                        </li>
                    )}

                    {/* Change Password - Available to all logged-in users */}
                    <li>
                        <button
                            onClick={() => handleNavigation('change-password')}
                            className={getNavButtonClasses(currentView === 'change-password')}
                            title={isSidebarCollapsed ? 'Change Password' : ''}
                        >
                            <Lock className="w-4 h-4" />
                            {!isSidebarCollapsed && 'Change Password'}
                            </button>
                    </li>
                </ul>
            </nav>
            {/* Logout Confirmation Modal */}
            <LogoutConfirmModal
                show={showLogoutConfirm}
                onConfirm={() => {
                    setShowLogoutConfirm(false);
                    onLogout();
                }}
                onCancel={() => setShowLogoutConfirm(false)}
            />
            {/* Delete Confirmation Modal */}
            <ConfirmDeleteModal
                show={showDeleteConfirm}
                onConfirm={handleConfirmDelete}
                onCancel={() => {
                    setShowDeleteConfirm(false);
                    setDeleteEntity(null);
                    setDeleteEntityType('');
                }}
                entityName={deleteEntity?.name || (deleteEntityType === 'Appointment' ? patients.find(p => p.id === deleteEntity?.patientId)?.name || 'N/A' : 'N/A')}
                entityType={deleteEntityType}
            />
            {/* Main Content Area */}
            <main className={`flex-1 p-4 bg-gray-50 dark:bg-gray-900 min-h-screen transition-all duration-300 ease-in-out ${
                isSidebarCollapsed ? 'md:ml-16' : 'md:ml-64'
            }`}>
                <header className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700 p-3 rounded-lg mb-4 flex items-center">
                    <button
                      onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                      className="md:hidden text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white mr-4"
                    >
                      <Menu className="w-6 h-6" />
                    </button>
                    {/* Desktop sidebar toggle */}
                    <button
                      onClick={toggleSidebarCollapse}
                      className="hidden md:block text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white mr-4"
                      title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    >
                      <Menu className="w-5 h-5" />
                    </button>
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                        {appSettings.organizationName || 'Loading...'} - {formatViewName(currentView)}
                    </h2>
                    {/* Global Quick Search removed */}
                    <div className="flex items-center gap-4">
                        {/* Theme Toggle */}
                        <ThemeToggle />
                        
                        {/* Notification Bell */}
                        <div className="relative">
                            <NotificationBell />
                            <NotificationPanel />
                        </div>
                        
                        {/* User Role Badge */}
                        {currentUser && (
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                currentUser.role === 'superadmin' ? 'bg-purple-100 text-purple-800' :
                                currentUser.role === 'org_admin' ? 'bg-blue-100 text-blue-800' :
                                currentUser.role === 'doctor' ? 'bg-green-100 text-green-800' :
                                currentUser.role === 'nurse' ? 'bg-pink-100 text-pink-800' :
                                currentUser.role === 'receptionist' ? 'bg-yellow-100 text-yellow-800' :
                                currentUser.role === 'pharmacist' ? 'bg-indigo-100 text-indigo-800' :
                                'bg-gray-100 text-gray-800'
                            }`}>
                                {currentUser.role.replace('_', ' ').toUpperCase()}
                            </span>
                        )}
                        <div className="w-6 h-6 bg-blue-200 rounded-full flex items-center justify-center text-blue-700 font-medium text-xs">
                            {currentUser ? currentUser.username.substring(0, 2).toUpperCase() : 'U'}
                        </div>
                        <span className="text-sm text-gray-700 dark:text-gray-300">{currentUser ? currentUser.username : 'User'}</span>
                        <button
                            onClick={() => setShowLogoutConfirm(true)}
                            className="text-gray-600 hover:text-gray-900 dark:text-gray-100 flex items-center gap-1"
                            title="Logout"
                        >
                            <LogOut className="w-5 h-5" />
                        </button>
                    </div>
                </header>
                {renderView()}
            </main>

            {/* Keyboard Shortcuts Help Modal */}
            <KeyboardShortcutsHelp
                isOpen={showKeyboardHelp}
                onClose={() => setShowKeyboardHelp(false)}
            />
        </div>
    );
};

export default HospitalManagementAppContent;